import {
  FilesetResolver,
  HandLandmarker,
  type HandLandmarkerResult,
} from "@mediapipe/tasks-vision";
import {
  HAND_COUNT,
  HAND_MODEL_PATH,
  HAND_PRESENCE_THRESHOLD,
  WASM_PATH,
} from "../config/constants.ts";
import { mirrorLandmarks } from "../gestures/classifiers.ts";
import type { HandFrame } from "../utils/types.ts";
import { smoothLandmarks } from "./landmarkSmoothing.ts";

export class HandTracker {
  private landmarker: HandLandmarker | null = null;
  private lastTimestamp = -1;
  private smoothed: LandmarkBag[] = [];
  lastProcessMs = 0;
  lastError: string | null = null;

  async init(): Promise<void> {
    const vision = await FilesetResolver.forVisionTasks(WASM_PATH);
    const options = {
      runningMode: "VIDEO" as const,
      numHands: HAND_COUNT,
      minHandDetectionConfidence: 0.55,
      minHandPresenceConfidence: HAND_PRESENCE_THRESHOLD,
      minTrackingConfidence: 0.5,
    };
    try {
      this.landmarker = await HandLandmarker.createFromOptions(vision, {
        ...options,
        baseOptions: { modelAssetPath: HAND_MODEL_PATH, delegate: "GPU" },
      });
    } catch {
      this.landmarker = await HandLandmarker.createFromOptions(vision, {
        ...options,
        baseOptions: { modelAssetPath: HAND_MODEL_PATH, delegate: "CPU" },
      });
    }
  }

  detect(
    video: HTMLVideoElement,
    timestamp: number,
    mirror: boolean,
    smoothing: number,
  ): HandFrame[] {
    if (!this.landmarker) return [];
    if (timestamp <= this.lastTimestamp) timestamp = this.lastTimestamp + 1;
    const started = performance.now();
    try {
      const result: HandLandmarkerResult = this.landmarker.detectForVideo(video, timestamp);
      this.lastTimestamp = timestamp;
      this.lastError = null;
      this.lastProcessMs = performance.now() - started;
      const frames: HandFrame[] = [];
      const count = result.landmarks?.length ?? 0;
      for (let i = 0; i < count; i += 1) {
        const raw = result.landmarks[i] ?? [];
        const category = result.handedness[i]?.[0];
        const label = category?.categoryName === "Left" ? "Left" : "Right";
        const score = category?.score ?? 0;
        const mappedRaw = raw.map((p) => ({ x: p.x, y: p.y, z: p.z ?? 0 }));
        const mapped = mirror ? mirrorLandmarks(mappedRaw) : mappedRaw;
        const prev = this.smoothed[i]?.landmarks ?? null;
        const landmarks = smoothLandmarks(prev, mapped, smoothing / 10);
        frames.push({ handedness: label, score, landmarks });
      }
      this.smoothed = frames;
      return frames;
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : "Hand tracker failed.";
      this.lastProcessMs = performance.now() - started;
      return [];
    }
  }

  reset(): void {
    this.smoothed = [];
    this.lastTimestamp = -1;
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
    this.reset();
  }
}

interface LandmarkBag {
  landmarks: HandFrame["landmarks"];
}
